import type { Faire, Season } from '@/content/renaissance-faires/types'

import { haversineMiles } from './distance'
import { withSchedules, type ScheduledFaire } from './schedule'

/** Where the visitor is searching from. */
export interface FinderOrigin {
    lat: number
    lng: number
    /** Display label, e.g. "Austin" or "your location". */
    label: string
}

export type SortMode = 'soonest' | 'nearest' | 'state'

export interface FinderFilters {
    season: Season | null
    /** Two-letter state code, or `null` for all states. */
    state: string | null
    origin: FinderOrigin | null
    sort: SortMode
}

/**
 * How far out a "near me" search reaches, in miles.
 *
 * Wide on purpose: faires are rural and thin on the ground, so a 50-mile radius
 * returns nothing across most of the country and reads as "there are none". At
 * 250 miles the answer is a drive or a bus ride away, which is the question the
 * page is actually answering.
 */
export const NEARBY_RADIUS_MILES = 250

export interface FinderResult extends ScheduledFaire {
    /** Straight-line miles from the origin, or `null` when there is no origin. */
    distanceMiles: number | null
}

/**
 * Apply the finder's filters, then sort.
 *
 * An explicit state filter beats the origin radius: picking "Michigan" from the
 * dropdown after searching a city means "show me Michigan", not "show me the
 * part of Michigan within 250 miles of where I am".
 */
export function findFaires(faires: Faire[], filters: FinderFilters, today: string): FinderResult[] {
    const { season, state, origin, sort } = filters
    const results: FinderResult[] = withSchedules(faires, today)
        .filter((faire) => {
            if (season && !faire.seasons.includes(season)) {
                return false
            }
            if (state && faire.state !== state) {
                return false
            }
            return true
        })
        .map((faire) => ({
            ...faire,
            distanceMiles: origin ? haversineMiles(origin, faire) : null,
        }))
        .filter((faire) => {
            if (!origin || state) {
                return true
            }
            return (faire.distanceMiles ?? Infinity) <= NEARBY_RADIUS_MILES
        })

    if (sort === 'nearest' && origin) {
        return results.sort((a, b) => (a.distanceMiles ?? 0) - (b.distanceMiles ?? 0))
    }
    if (sort === 'state') {
        return results.sort((a, b) =>
            `${a.stateName}${a.name}`.localeCompare(`${b.stateName}${b.name}`),
        )
    }
    return results.sort((a, b) => a.schedule.sortKey - b.schedule.sortKey)
}

const SORT_LABELS: Record<SortMode, string> = {
    soonest: 'soonest first',
    nearest: 'nearest first',
    state: 'A–Z by state',
}

/**
 * The line above the results ("14 faires in Michigan · fall · soonest first").
 * Stating the active filters back is what makes an empty or short list read as
 * an answer rather than a bug.
 */
export function resultSummary(
    count: number,
    filters: FinderFilters,
    stateNames: Record<string, string>,
): string {
    const parts = [`${count} faire${count === 1 ? '' : 's'}`]
    if (filters.state) {
        parts.push(` in ${stateNames[filters.state] ?? filters.state}`)
    } else if (filters.origin) {
        parts.push(` within ${NEARBY_RADIUS_MILES} mi of ${filters.origin.label}`)
    }
    if (filters.season) {
        parts.push(` · ${filters.season}`)
    }
    parts.push(` · ${SORT_LABELS[filters.sort]}`)
    return parts.join('')
}
